import { useState } from 'react';
import { Star, CheckCircle } from 'lucide-react';
import { SEO } from '@/components/SEO';
import { Footer } from '@/sections/Footer';
import { useReveal } from '@/lib/useReveal';
import { Section, Container, Display, Lead } from '@/components/system';
import { submitForm } from '@/lib/forms';

/**
 * PROMO PAGE (temporary, added 2026-08-19) — the agency-ratings contest.
 * People rate the agency they've shipped with; submitting enters them.
 * FormType 'valoracion' → websiteFormSubmittions → email (Trigger Email ext).
 * When the promo ends: remove this page's route + STATIC_ROUTES entry + the Home banner.
 *
 * Renamed 2026-08-23: /sorteo → /concurso-agencias-conocidas ("sorteo" means a
 * random draw, which is the word that pulls a prize promotion into US lottery
 * law). NOTE: renaming the route did NOT change the mechanic — if the winner is
 * still picked at random from entrants, the prize + chance + consideration test
 * still applies. Decide the mechanic (skill-based ranking vs. draw with a free
 * alternative method of entry) and publish the rules before the next round.
 */
const RATING_LABELS = ['', 'Muy mala', 'Mala', 'Regular', 'Buena', 'Excelente'];

function Stars({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [hover, setHover] = useState(0);
  const active = hover || value;
  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-1" role="radiogroup" aria-label="Calificación de 1 a 5 estrellas">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={value === n}
            aria-label={`${n} de 5 — ${RATING_LABELS[n]}`}
            onClick={() => onChange(n)}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            className="p-1 -m-0.5"
          >
            <Star
              className={`w-8 h-8 transition-colors ${n <= active ? 'fill-amber-400 text-amber-400' : 'fill-transparent text-line'}`}
            />
          </button>
        ))}
      </div>
      <span className="text-sm font-medium text-text-secondary min-w-[80px]">
        {active ? RATING_LABELS[active] : ''}
      </span>
    </div>
  );
}

export function ConcursoAgencias() {
  const heroRef = useReveal<HTMLElement>();
  const [rating, setRating] = useState(0);
  const [form, setForm] = useState({ nombre: '', email: '', telefono: '', agencia: '', ciudad: '', envio: '', experiencia: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [ratingMissing, setRatingMissing] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rating) { setRatingMissing(true); return; }
    setStatus('loading');
    try {
      await submitForm('valoracion', {
        ...form,
        calificacion: `${rating} / 5 — ${RATING_LABELS[rating]}`,
      });
      setStatus('success');
    } catch (err) {
      console.error('[ConcursoAgencias] submission failed:', err);
      setStatus('error');
    }
  };

  const input =
    'w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand bg-white';

  return (
    <main className="bg-bg-light">
      <SEO
        title="Concurso | Valora tu Agencia de Envíos | Mi Envío Cuba"
        description="Participa en el concurso de Mi Envío Cuba: califica cómo tu agencia de envíos manejó tus paquetes y entra automáticamente. Toma menos de dos minutos."
        path="/concurso-agencias-conocidas"
        keywords="concurso Mi Envío Cuba, valorar agencia de envíos, calificar agencia envíos Cuba, opiniones agencias de envíos"
      />

      <Section tone="white" ref={heroRef} className="pt-[110px] sm:pt-[130px] pb-16 sm:pb-24">
        <Container>
          <div className="mx-auto max-w-3xl text-center mb-12">
            <p data-reveal className="inline-flex items-center gap-2 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold uppercase tracking-wider px-3 py-1.5 mb-5">
              <Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />
              Concurso · Programa de valoraciones
            </p>
            <Display data-reveal as="h1" size="lg">Valora tu agencia. Participa en el concurso.</Display>
            <Lead data-reveal className="mt-5 mx-auto max-w-2xl">
              ¿Has enviado paquetes a Cuba? Califica cómo tu agencia manejó tus envíos y quedas
              participando automáticamente. Toma menos de dos minutos.
            </Lead>
            <ul data-reveal className="mt-6 flex flex-wrap justify-center gap-x-8 gap-y-2 text-text-secondary text-sm">
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-brand" /> Una participación por persona.</li>
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-brand" /> Buenas y malas experiencias cuentan.</li>
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-brand" /> Te contactamos por correo si ganas.</li>
            </ul>
          </div>

          <div data-reveal className="mx-auto max-w-2xl rounded-[28px] bg-bg-light p-7 sm:p-10">
            {status === 'success' ? (
              <div className="text-center py-14">
                <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-4" />
                <h2 className="font-heading text-2xl font-semibold text-text-primary mb-2">¡Estás participando!</h2>
                <p className="text-text-secondary max-w-md mx-auto">
                  Recibimos tu valoración. Si resultas ganador, te contactamos al correo que dejaste.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">Tu nombre *</label>
                    <input name="nombre" required value={form.nombre} onChange={handleChange} className={input} placeholder="Tu nombre" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">Tu correo *</label>
                    <input name="email" type="email" required value={form.email} onChange={handleChange} className={input} placeholder="tu@email.com" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">Agencia con la que enviaste *</label>
                    <input name="agencia" required value={form.agencia} onChange={handleChange} className={input} placeholder="Nombre de la agencia" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">Ciudad de la agencia</label>
                    <input name="ciudad" value={form.ciudad} onChange={handleChange} className={input} placeholder="Miami, Hialeah…" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">¿Qué enviaste?</label>
                  <select name="envio" value={form.envio} onChange={handleChange} className={input}>
                    <option value="">Seleccionar...</option>
                    <option value="Paquetes">Paquetes</option>
                    <option value="Alimentos / medicinas">Alimentos / medicinas</option>
                    <option value="Varios">Varios</option>
                    <option value="Otro">Otro</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">¿Cómo manejó tus envíos? *</label>
                  <Stars value={rating} onChange={(v) => { setRating(v); setRatingMissing(false); }} />
                  {ratingMissing && <p className="mt-1 text-sm text-red-600">Elige una calificación de 1 a 5 estrellas.</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">Tu experiencia *</label>
                  <textarea
                    name="experiencia" required value={form.experiencia} onChange={handleChange} rows={4} className={input}
                    placeholder="¿Llegó todo? ¿A tiempo? ¿Te mantuvieron informado? Cuéntanos cómo fue…"
                  />
                </div>
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="inline-flex items-center justify-center rounded-full bg-brand text-white font-medium px-7 h-12 hover:bg-brand-deep transition-colors disabled:opacity-60 w-full"
                >
                  {status === 'loading' ? 'Enviando…' : 'Enviar y participar'}
                </button>
                {status === 'error' && (
                  <p className="text-sm text-red-600 text-center">No se pudo enviar. Intenta de nuevo en un momento.</p>
                )}
                <p className="text-xs text-text-tertiary text-center">
                  Usamos tu correo solo para contactarte sobre el concurso.
                </p>
              </form>
            )}
          </div>
        </Container>
      </Section>

      <Footer />
    </main>
  );
}
